import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIVE_TAB, PRESCRIPTION_STATUS } from '../interfaces/prescription';
import { get, max } from 'lodash';
import { setAuthorizationHeader, setPrescriptionIdHeader, workspace } from '../shared/api';
import useValidation, { UseValidation } from '../helpers/useValidation';
import { extractPatientAge } from '../helpers/patientAge';
import { PrescriberService } from '../shared/prescriber';
import { Tracker } from '../shared/tracker/Tracker';
import { encryptToMd5, extractLoginFromToken } from '../helpers/string';
import { getPathname } from '../helpers/getPathname';
import { useLoading } from '../helpers/useLoading';
import { isBasicAuthentication, isCookieAuthentication } from '../helpers/isCookieAuthentication';
import Cookies from 'js-cookie';
import { useHistory } from 'react-router-dom';
import { useTimer } from '../helpers/useTimer/useTimer';
import { ThemeContext } from '../shared/theme/ThemeProvider';
import { FeedbackContext } from './FeedbackProvider';
import { CanceledPrescription } from '../components/CanceledPrescription';
import { sendCancelPostMessage } from '../helpers/window';
import { isRunningInIframe } from '../helpers/isRunningInIframe';
import { usePrescriptionStore } from '../stores/prescriptionStore';
import { logger } from '../helpers/logger';
import { normalizeMedicineIcd } from '../helpers/medicineIcds';
import { usePathStore } from '../stores/pathStore';
import { ManualMedicine, Medicine } from '../interfaces/medicine';
import { AddMedicinePayload, EditMedicinePayload } from '../components/search/medicineService';
import { useModelStore } from '../stores/modelStore';
import { DateTime } from 'luxon';
import { ExamService } from '../components/search/examService';
import { useExamStore } from '../stores/examStore';
import { savePrescriptionStartedPayload, getPrescriptionStartedPayload, savePrescriptionCompletedPayload, amplitudeModule, buildDefaultAmplitudeProperties } from '../shared/amplitude';
import { appViewportHeight } from '../helpers/embeddedViewport';

/**
 * Aguarda o cookie estar disponível com polling.
 * Resolve o race condition entre o Set-Cookie do redirect e a disponibilidade via JavaScript.
 * @param cookieName Nome do cookie a ser verificado
 * @param maxAttempts Número máximo de tentativas (default: 10)
 * @param intervalMs Intervalo entre tentativas em ms (default: 300)
 * @returns O valor do cookie ou null se não encontrado após todas as tentativas
 */
const waitForCookie = async (cookieName: string, maxAttempts = 10, intervalMs = 300): Promise<string | null> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const cookie = Cookies.get(cookieName);
    if (cookie) {
      return cookie;
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }
  return null;
};

declare function Genesys(
  command: string,
  action: string,
  options: Record<string, any>,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): void;
export interface PrescriptionContextProps {
  localCertificateValidation: UseValidation;
  prescriptionLoading: boolean;
  helpChatState: 'opened' | 'closed';
  setHelpChatState(value: 'opened' | 'closed'): void;
  loadingPrescriberCoreData: boolean;
  loadingModel: boolean;
  allItems: {
    allMedicines: (Medicine | ManualMedicine)[];
    nextOrder: number;
  };
  showCanceledPrescriptionDialog(): void;
}

export const PrescriptionContext = createContext<PrescriptionContextProps>({
  localCertificateValidation: {
    errorMessage: '',
    hasError: false,
    validate: () => true,
  },
  prescriptionLoading: false,
  helpChatState: 'closed',
  setHelpChatState: (value: 'opened' | 'closed') => { },
  loadingPrescriberCoreData: false,
  loadingModel: false,
  allItems: {
    allMedicines: [],
    nextOrder: 0,
  },
  showCanceledPrescriptionDialog: () => { },
});

export function PrescriptionProvider(props: { children: ReactNode }) {
  const hasBootstrappedRef = useRef(false);

  const [prescriptionLoading, runWithLoadingPrescription] = useLoading();
  const [helpChatState, setHelpChatState] = useState<'opened' | 'closed'>('closed');
  const [loadingPrescriberCoreData, runWithLoadingPrescriberCoreData] = useLoading();
  const [loadingModel, runWithLoadingModel] = useLoading();

  const { fetchModels, fetchSponsoredModels } = useModelStore();

  const {
    setBenefits,
    fetchPrescriptionByReference,
    getPrescriberCoreData,
    prescriptionTokenExpirationData,
    setRqes,
    setSelectedRqe,
    setInitialActiveTab,
  } = usePrescriptionStore();

  const prescription = usePrescriptionStore((store) => store.prescription);
  const medicineBeneficiosEnrichment = usePrescriptionStore((store) => store.medicineBeneficiosEnrichment ?? {});

  const { setGeneralExamIcd, setGeneralExamObservation } = useExamStore();

  const primaryColor = usePrescriptionStore((store) => store.primaryColor());
  const secondaryColor = usePrescriptionStore((store) => store.secondaryColor());

  const { setThemeColor } = useContext(ThemeContext);
  const { openBaseDialog } = useContext(FeedbackContext);

  const { setPath } = usePathStore();

  const history = useHistory();

  const { startTimer } = useTimer({
    onTimeOver: () => {
      history.push('/notfound');
    },
  });

  const localCertificateValidation = useValidation({
    errorMessage: '',
    validate(val: any): boolean {
      return !!val;
    },
  });

  const prescriptionReference = useMemo(() => {
    if (isCookieAuthentication() || isBasicAuthentication()) {
      return get(getPathname().split('/'), [3], null);
    }

    return get(getPathname().split('/'), [2], null);
  }, []);

  const [authorizationToken, setAuthorizationToken] = useState<string | null>(() => {
    // Inicialização síncrona para autenticação básica (v2)
    if (isBasicAuthentication()) {
      return get(getPathname().split('/'), [4], null);
    }
    // Para cookie auth (v3), tenta ler imediatamente mas pode não estar disponível ainda
    if (isCookieAuthentication() && prescriptionReference) {
      const cookie = Cookies.get(`prescription-${prescriptionReference}-jwt`);
      if (cookie) {
        return `jwt-mevo-${cookie}`;
      }
    }
    return null;
  });

  const allItems = useMemo(() => {
    if (!prescription) {
      return { allMedicines: [], nextOrder: 1 };
    }

    const merged = [...prescription.Itens, ...prescription.ItensManuais].sort((firstMedicine, secondMedicine) => {
      if (firstMedicine.Ordem && secondMedicine.Ordem) {
        return firstMedicine.Ordem - secondMedicine.Ordem;
      }

      if (!firstMedicine.DataAdicao || !secondMedicine.DataAdicao) return 1;

      return DateTime.fromJSDate(new Date(firstMedicine.DataAdicao)).toFormat('dd/MM/yyyy') <
        DateTime.fromJSDate(new Date(secondMedicine.DataAdicao)).toFormat('dd/MM/yyyy')
        ? -1
        : 1;
    });

    const medicines = merged.map((item) => {
      const id = 'IdMedicamento' in item ? item.IdMedicamento : null;
      const needsEnrichment = id != null && (!item.Beneficios || item.Beneficios.length === 0);
      const enriched = needsEnrichment && medicineBeneficiosEnrichment[id]?.length
        ? { ...item, Beneficios: medicineBeneficiosEnrichment[id] }
        : item;
      return enriched;
    });

    const maxOrder = max(medicines.map((m) => m.Ordem || 0)) || 0;

    return { allMedicines: medicines, nextOrder: maxOrder + 1 };
  }, [prescription, medicineBeneficiosEnrichment]);

  useEffect(() => {
    if (!authorizationToken) return;

    setAuthorizationHeader(authorizationToken);

    Tracker.peopleSet('parceiro', extractLoginFromToken(authorizationToken));
  }, [authorizationToken]);

  useEffect(() => {
    if (isCookieAuthentication()) {
      if (prescriptionTokenExpirationData) {
        startTimer(
          new Date(prescriptionTokenExpirationData.expiresAt).getTime(),
          prescriptionTokenExpirationData.timeZone
        );
      }

      setPath(`/v3/prescription/${prescriptionReference}`);
      return;
    }
    setPath(`/v2/prescription/${prescriptionReference}/${authorizationToken}`);
  }, [authorizationToken, prescriptionReference, prescriptionTokenExpirationData, setPath, startTimer]);

  const showCanceledPrescriptionDialog = useCallback(async () => {
    const isRunningOnIframe = isRunningInIframe();

    return openBaseDialog(
      () => <CanceledPrescription onConfirm={sendCancelPostMessage} showConfirmButton={isRunningOnIframe} />,
      {
        width: appViewportHeight(0.95),
        height: appViewportHeight(0.95),
        persist: true,
        backgroundColor: 'transparent',
      }
    );
  }, [openBaseDialog]);

  useEffect(() => {
    // Guard de idempotência: garante que o bootstrap (autenticação + busca da prescrição) rode
    // uma única vez por carregamento. Sem isso, o StrictMode (e re-renders que mudem as deps)
    // dispara duas execuções concorrentes que competem pela troca do token de uso único
    // (GET /prescricao/token/{token}): a 2ª recebe 404 e cai indevidamente no /notfound.
    if (hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;

    const run = async () => {
      const prescription = await runWithLoadingPrescription(async () => {
        try {
          let jwt: string | null = null;

          /**
           * Fluxo de autenticação:
           * 1. Se já tem token (v2 ou cookie já disponível), usa ele
           * 2. Se é cookie auth (v3) e cookie não está disponível, aguarda com polling
           * 3. Se ainda não tem JWT, tenta fluxo alternativo via ?token= (iOS/Mac)
           */

          // 1. Verificar se já tem token configurado
          if (authorizationToken) {
            jwt = authorizationToken.replace(/^jwt-mevo-/, '');
          }

          // 2. Se é cookie auth e ainda não tem JWT, aguardar o cookie (race condition do Set-Cookie)
          if (!jwt && isCookieAuthentication() && prescriptionReference) {
            const cookieName = `prescription-${prescriptionReference}-jwt`;

            // Polling: aguarda até 3 segundos para o cookie aparecer
            const cookie = await waitForCookie(cookieName, 10, 300);

            if (cookie) {
              jwt = cookie;
              setAuthorizationToken(`jwt-mevo-${jwt}`);
              setAuthorizationHeader(`jwt-mevo-${jwt}`);
            }
          }

          // 3. Se ainda não tem JWT, tentar fluxo alternativo (iOS/Mac com ?token=)
          if (!jwt && prescriptionReference) {
            const params = new URLSearchParams(history.location.search);
            const token = params.get('token');

            if (token) {
              try {
                const { data } = await workspace.get(`/prescricao/token/${token}`);
                jwt = data.jwt;

                const inFiftyFiveMinutes = new Date(new Date().getTime() + 55 * 60 * 1000);
                Cookies.set(`prescription-${prescriptionReference}-jwt`, jwt as string, {
                  expires: inFiftyFiveMinutes,
                });

                setAuthorizationToken(`jwt-mevo-${jwt}`);
                setAuthorizationHeader(`jwt-mevo-${jwt}`);
              } catch (tokenError: any) {
                logger.error('Erro ao obter JWT via token temporário:', tokenError);
                // A troca pode falhar (ex.: 404 de token de uso único já consumido por uma
                // execução concorrente). Nesse caso o cookie pode já ter sido setado por ela —
                // re-checa antes de desistir.
                const existingCookie = Cookies.get(`prescription-${prescriptionReference}-jwt`);
                if (existingCookie) {
                  jwt = existingCookie;
                  setAuthorizationToken(`jwt-mevo-${jwt}`);
                  setAuthorizationHeader(`jwt-mevo-${jwt}`);
                }
              }
            }
          }

          // 4. Última chance: uma execução concorrente (ou outra aba) pode ter setado o cookie.
          if (!jwt && isCookieAuthentication() && prescriptionReference) {
            const lastChanceCookie = Cookies.get(`prescription-${prescriptionReference}-jwt`);
            if (lastChanceCookie) {
              jwt = lastChanceCookie;
              setAuthorizationToken(`jwt-mevo-${jwt}`);
              setAuthorizationHeader(`jwt-mevo-${jwt}`);
            }
          }

          // 5. Se ainda não tem JWT, não há como autenticar
          if (!jwt) {
            logger.error('Falha na autenticação: cookie não disponível e token não fornecido', {
              prescriptionReference,
              isCookieAuth: isCookieAuthentication(),
              hasTokenParam: !!new URLSearchParams(history.location.search).get('token'),
            });
            history.push('/notfound');
            return null;
          }

          // 6. Buscar prescrição com JWT autenticado
          const prescription = await fetchPrescriptionByReference(prescriptionReference);

          if (prescription && prescription.idPrescricaoStatus === PRESCRIPTION_STATUS.CANCELED) {
            showCanceledPrescriptionDialog();
          }

          if (prescription && prescription.Prescritor.RQEs) {
            setRqes(prescription.Prescritor.RQEs);
          }
          if (prescription && prescription.RQESelecionado) {
            setSelectedRqe(prescription.RQESelecionado.idMedicoRqe);
          }

          return prescription;
        } catch (e: any) {
          logger.error('Erro ao carregar prescrição:', {
            error: e?.message || e,
            status: e?.response?.status,
            prescriptionReference,
          });
          history.push('/notfound');
        }
      });

      if (!prescription) return;

      setPrescriptionIdHeader(prescription.idPrescricao);
      if (prescription.AbaAtiva && prescription.AbaAtiva !== ACTIVE_TAB.PRESCRIPTION) {
        setInitialActiveTab(prescription.AbaAtiva);
      }

      if (prescription.TemBeneficio) {
        const benefits = await PrescriberService.getBenefits();
        setBenefits(benefits);
      }

      const age = extractPatientAge(prescription.Paciente?.Idade);

      runWithLoadingModel(async () => {
        await fetchModels(prescription.Prescritor.idPrescritor, prescription.idPrescricao, age);
        await fetchSponsoredModels(prescription.Prescritor.idPrescritor, prescription.idPrescricao, age);
      });

      runWithLoadingPrescriberCoreData(async () => {
        try {
          await getPrescriberCoreData({
            prescriptionId: prescription.idPrescricao,
            prescriberId: prescription.Prescritor.idPrescritor,
            prescriberDocument: prescription.Prescritor.Documento,
            lastSignatureMethod: prescription.UltimoMetodoAssinatura,
            isDigitalCertificateRequired: prescription.CertificadoDigitalObrigatorio,
          });
        } finally {
          const prescriberHash = usePrescriptionStore.getState().prescriberConfigurations?.Hash;
          amplitudeModule.identify(prescriberHash || String(prescription.Prescritor.idPrescritor));
          amplitudeModule.setUserProperties(buildDefaultAmplitudeProperties());
        }
      });

      try {
        const generalExamData = await ExamService.getGeneralExamData(prescription.idPrescricao);

        const existingIcd = get(generalExamData, 'CID10Estruturado', null);
        const existingObservation = get(generalExamData, 'Instrucoes', '');

        setGeneralExamIcd(existingIcd);
        setGeneralExamObservation(existingObservation);

        if (!existingIcd && prescription.CIDS10Estruturados && prescription.CIDS10Estruturados.length > 0) {
          const prescriptionIcd = prescription.CIDS10Estruturados[0];

          setGeneralExamIcd(prescriptionIcd);

          try {
            await ExamService.updateGeneralDataExam(prescription.idPrescricao, {
              Instrucoes: existingObservation,
              CID10Estruturado: {
                Codigo: prescriptionIcd.Codigo,
                idCID10: prescriptionIcd.idCID10,
                Descricao: prescriptionIcd.Descricao,
              },
            });
          } catch (error) { }
        }
      } catch (error) { }



      const crm = encryptToMd5(prescription.Prescritor.Numero);

      Tracker.identify(crm, { name: prescription.Prescritor.Nome });

      Tracker.register({
        ePrescription: prescription.idPrescricao,
        CRM: crm,
        specialty: prescription.Prescritor.Especialidades,
        partner: extractLoginFromToken(get(getPathname().split('/'), [4], '')),
      });

      Tracker.peopleSetOnce('First Started Prescription Date', new Date());

      const prescriberConfiguration = usePrescriptionStore.getState().prescriberConfiguration;

      const amplitudePayload = {
        validation_code: prescription.CodigoValidacao,
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription.Estabelecimento?.Nome ?? '',
      };

      const alreadyTracked = getPrescriptionStartedPayload(prescription.idPrescricao);
      if (!alreadyTracked) {
        amplitudeModule.prescriptionStarted(amplitudePayload);
        savePrescriptionStartedPayload(prescription.idPrescricao, amplitudePayload);
      } else {
        amplitudeModule.prescriptionDraftResume({
          validation_code: prescription.CodigoValidacao,
          partner_id: prescriberConfiguration?.idParceiro ?? 0,
          partner_name: prescriberConfiguration?.Nome ?? '',
          establishment_name: prescription.Estabelecimento?.Nome ?? '',
        });
      }

      savePrescriptionCompletedPayload(prescription.idPrescricao, {
        validation_code: prescription.CodigoValidacao,
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription.Estabelecimento?.Nome ?? '',
        notes: prescription.Comentario ?? '',
        is_generic_name_activated: prescription.ExibirPorPrincipioAtivo,
        medications: [],
        compounded_medication: [],
        compounded_medications: [],
        medical_tests: [],
        medical_tests_CID: [],
        medical_tests_clinical_indication: '',
        doctors_note: [],
        referral: [],
        medical_report: [],
        protocol: [],
      });
    };

    run();
  }, [
    history,
    fetchPrescriptionByReference,
    prescriptionReference,
    runWithLoadingPrescription,
    getPrescriberCoreData,
    runWithLoadingPrescriberCoreData,
    authorizationToken,
    showCanceledPrescriptionDialog,
    setBenefits,
    setRqes,
    setSelectedRqe,
    setInitialActiveTab,
    runWithLoadingModel,
    fetchModels,
    setGeneralExamIcd,
    setGeneralExamObservation,
    fetchSponsoredModels,
  ]);

  useEffect(() => {
    // este useEffect deve permanecer separado do mount por motivos de:
    // primaryColor e secondaryColor precisam do prescription para executar a validação de cores
    // no mount existirá dois estados do prescriptionState, o default e o pós mount
    // caso esta linha abaixo ficar no useEffect de mount as APIS vão ser chamadas duas vezes, prejudicando a performance do app
    setThemeColor({ primary: primaryColor, secondary: secondaryColor });
  }, [primaryColor, secondaryColor, setThemeColor]);

  useEffect(() => {
    if (typeof Genesys !== 'function') return;

    if (helpChatState === 'opened') {
      try {
        Genesys('command', 'Messenger.open', {});
        Genesys('subscribe', 'Messenger.closed', () => {
          setHelpChatState('closed');
        });
      } catch (error) {
        // Ignora erros do Genesys para não poluir o console
        logger.log('Genesys Messenger.open error (ignored):', error);
      }
    }
    // Não tenta fechar o messenger quando o estado é 'closed'
    // O erro "Command does not exist" ocorre quando tenta fechar um messenger já fechado ou nunca aberto
    // O messenger será fechado automaticamente quando o usuário clicar no botão de fechar
  }, [helpChatState]);

  const isColorsLoaded = !!prescription;

  return (
    <PrescriptionContext.Provider
      value={{
        localCertificateValidation,
        prescriptionLoading,
        helpChatState,
        setHelpChatState,
        loadingPrescriberCoreData,
        loadingModel,
        allItems,
        showCanceledPrescriptionDialog,
      }}
    >
      {isColorsLoaded ? props.children : null}
    </PrescriptionContext.Provider>
  );
}

export function adaptMedicineToAddPrescriptionParam(medicine: Medicine): AddMedicinePayload {
  const structuredPacking = medicine.EmbalagemEstruturada;

  return {
    medicamentoLme: medicine.medicamentoLme,
    Comentario: '',
    Embalagem: medicine.Embalagem || '',
    idLaboratorio: medicine.idLaboratorioSelecionado ?? null,
    UsoContinuo: medicine.UsoContinuo,
    IdMedicamento: medicine.IdMedicamento,
    PermitirTroca: medicine.PermitirTroca === 1,
    Quantidade: medicine.Quantidade || '',
    Posologia: medicine.Posologia,
    ReferenciaExterna: '',
    TemBeneficio: medicine.TemBeneficio,
    Ordem: medicine.Ordem,
    ParanaSemDor: medicine.ParanaSemDor,
    medicineIcd: normalizeMedicineIcd(medicine.medicineIcd),
    ...(medicine.Grupo && { Grupo: medicine.Grupo }),
    ...(structuredPacking.LabelEmbalagem && {
      LabelEmbalagem: structuredPacking.LabelEmbalagem,
    }),
    ...(structuredPacking.VolumeEmbalagem && {
      VolumeEmbalagem: structuredPacking.VolumeEmbalagem,
    }),
    ...(structuredPacking.QuantidadeEmbalagens && {
      QuantidadeEmbalagens: structuredPacking.QuantidadeEmbalagens,
    }),
    ...(structuredPacking.UnidadeVolumeEmbalagem && {
      UnidadeVolumeEmbalagem: structuredPacking.UnidadeVolumeEmbalagem,
    }),
    ...(structuredPacking.TipoEmbalagem && {
      TipoEmbalagem: structuredPacking.TipoEmbalagem,
    }),
    ...(medicine.isPremiumPlacement && {
      isPremiumPlacement: medicine.isPremiumPlacement,
    }),
    ...(medicine.posologySelectedFromSuggestion && {
      posologySelectedFromSuggestion: medicine.posologySelectedFromSuggestion,
    }),
    ...(medicine.posologySelectedFromSuggestionId && {
      posologySelectedFromSuggestionId: medicine.posologySelectedFromSuggestionId,
    }),
    ...(medicine.QuantidadeRecorrencia && {
      QuantidadeRecorrencia: medicine.QuantidadeRecorrencia,
    }),
    ...(medicine.isStructuredPosology !== undefined && {
      isStructuredPosology: medicine.isStructuredPosology,
    }),
    ...(medicine.PosologiaEstruturada && {
      PosologiaEstruturada: medicine.PosologiaEstruturada,
    }),
    ...(medicine.pbmConsentGiven !== undefined && {
      pbmConsentGiven: medicine.pbmConsentGiven,
    }),
  };
}

export function adaptMedicineToEditPrescriptionParam(medicine: Medicine): EditMedicinePayload {
  const structuredPacking = medicine.EmbalagemEstruturada;

  return {
    ...medicine,
    idLaboratorio: medicine.idLaboratorioSelecionado ?? null,
    medicineIcd: normalizeMedicineIcd(medicine.medicineIcd),
    ...(structuredPacking.LabelEmbalagem && {
      LabelEmbalagem: structuredPacking.LabelEmbalagem,
    }),
    ...(structuredPacking.VolumeEmbalagem && {
      VolumeEmbalagem: structuredPacking.VolumeEmbalagem,
    }),
    ...(structuredPacking.QuantidadeEmbalagens && {
      QuantidadeEmbalagens: structuredPacking.QuantidadeEmbalagens,
    }),
    ...(structuredPacking.UnidadeVolumeEmbalagem && {
      UnidadeVolumeEmbalagem: structuredPacking.UnidadeVolumeEmbalagem,
    }),
    ...(structuredPacking.TipoEmbalagem && {
      TipoEmbalagem: structuredPacking.TipoEmbalagem,
    }),
    ...(medicine.isPremiumPlacement && {
      isPremiumPlacement: medicine.isPremiumPlacement,
    }),
    ...(medicine.posologySelectedFromSuggestion && {
      posologySelectedFromSuggestion: medicine.posologySelectedFromSuggestion,
    }),
    ...(medicine.posologySelectedFromSuggestionId && {
      posologySelectedFromSuggestionId: medicine.posologySelectedFromSuggestionId,
    }),
    ...(medicine.isStructuredPosology !== undefined && {
      isStructuredPosology: medicine.isStructuredPosology,
    }),
    ...(medicine.PosologiaEstruturada && {
      PosologiaEstruturada: medicine.PosologiaEstruturada,
    }),
    ...(medicine.pbmConsentGiven !== undefined && {
      pbmConsentGiven: medicine.pbmConsentGiven,
    }),
  };
}
